import * as db from "../cache/db";

// IDB key prefixes / patterns — kept in sync with src/cache/routePreCache.js
// and src/hooks/usePlaceSearch.js.
export const KEY_FILTERS = {
  routes:   (k) => typeof k === "string" && /^route_/.test(k),
  stories:  (k) => typeof k === "string" && /^place_.+_story$/.test(k),
  audio:    (k) => typeof k === "string" && /^place_.+_audio$/.test(k),
  photos:   (k) => typeof k === "string" && /^place_.+_photo$/.test(k),
  searches: (k) => typeof k === "string" && /^place_search_/.test(k),
};

export const CACHE_NAMES = {
  mapTiles:    "map-tiles",
  geocoder:    "maptiler-geocoding",
  placePhotos: "place-photos",
  fonts:       "google-fonts",
};

// ─── Size primitives ────────────────────────────────────────────────────────
const blobSize = (v) => {
  if (v instanceof Blob) return v.size;
  if (v instanceof ArrayBuffer) return v.byteLength;
  if (v == null) return 0;
  try { return new Blob([JSON.stringify(v)]).size; } catch { return 0; }
};

export async function bytesByKeyFilter(predicate) {
  const all = await db.keys();
  const matching = all.filter(predicate);
  let total = 0;
  for (const key of matching) {
    const value = await db.get(key);
    total += blobSize(value);
  }
  return total;
}

export async function bytesInCache(cacheName) {
  if (!("caches" in window)) return 0;
  const has = await caches.has(cacheName);
  if (!has) return 0;
  const cache = await caches.open(cacheName);
  const reqs = await cache.keys();
  let total = 0;
  for (const req of reqs) {
    const res = await cache.match(req);
    if (!res) continue;
    const hdr = Number(res.headers.get("content-length"));
    if (hdr > 0) { total += hdr; continue; }
    try {
      const blob = await res.clone().blob();
      total += blob.size;
    } catch { /* ignore broken entries */ }
  }
  return total;
}

// ─── Aggregate breakdown ────────────────────────────────────────────────────
export async function computeStorageBreakdown() {
  const [
    stories, audio, photos, routes, searches,
    mapTiles, geocoder, placePhotos, fonts,
  ] = await Promise.all([
    bytesByKeyFilter(KEY_FILTERS.stories),
    bytesByKeyFilter(KEY_FILTERS.audio),
    bytesByKeyFilter(KEY_FILTERS.photos),
    bytesByKeyFilter(KEY_FILTERS.routes),
    bytesByKeyFilter(KEY_FILTERS.searches),
    bytesInCache(CACHE_NAMES.mapTiles),
    bytesInCache(CACHE_NAMES.geocoder),
    bytesInCache(CACHE_NAMES.placePhotos),
    bytesInCache(CACHE_NAMES.fonts),
  ]);

  const total = stories + audio + photos + routes + searches
              + mapTiles + geocoder + placePhotos + fonts;

  let quota = null;
  if (navigator.storage?.estimate) {
    try { quota = (await navigator.storage.estimate()).quota ?? null; } catch { /* ignore */ }
  }

  return {
    stories, audio, photos, routes, searches,
    mapTiles, geocoder, placePhotos, fonts,
    total, quota,
  };
}

export function formatBytes(bytes) {
  if (!bytes || bytes < 1024) return `${bytes || 0} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
